/**
 * App-wide ApiClient instance.
 *
 * Constructed once via `initApiClient(...)` and read everywhere else
 * via `getApiClient()`. The init step takes an `onAuthLost` callback
 * so this module doesn't have to know which router lives on top of
 * the app — `main.ts` is the one place that wires the navigation in.
 *
 * Why two-step (init + get) instead of a lazy module-singleton:
 *  - The previous lazy version imported `@/app/router` at module
 *    parse time, which meant any file touching `getApiClient` pulled
 *    the router (and its route children + their lazy chunks) into the
 *    boot graph. Tests and any future Storybook/SSR seam paid for
 *    transport setup they couldn't use.
 *  - With explicit init, this module's only structural dependencies
 *    are `@/shared/api`, `@/shared/lib/network`, and `useSessionStore`
 *    (a Pinia singleton, not a transport peer). The router is held
 *    behind a function reference passed in.
 *
 * Tests that need an isolated client still construct one directly via
 * `createApiClient(...)` — this provider is just the app's singleton.
 */
import type { ApiClient } from '@/shared/api'
import { ApiError, createApiClient } from '@/shared/api'
import { markRequestEnd, markRequestStart } from '@/shared/lib/network'
import { refreshRequest } from '@/features/auth/api/auth.api'
import { useSessionStore } from '@/app/stores/session.store'
import { env } from '@/app/config/env'

export interface InitApiClientOptions {
  /**
   * Called when the api client gives up on a session (refresh failed
   * after a 401, or a second 401 came back after the retry). `main.ts`
   * is expected to clear the session store and bounce to the login
   * route here — we keep the navigation concern out of this module.
   */
  onAuthLost(): void
}

let _client: ApiClient | null = null
/**
 * Held in module scope so `resetApiClient()` can rebuild the client
 * after the user picks a different host in DomainSelect without the
 * caller re-supplying the navigation hook.
 */
let _onAuthLost: (() => void) | null = null

function buildClient(): ApiClient {
  const session = useSessionStore()
  const baseURL = session.apiHost ?? env.apiHost
  const onAuthLost = _onAuthLost
  if (!onAuthLost) {
    throw new Error(
      'API client is not initialised — call initApiClient(...) from main.ts before any composable that uses the api.',
    )
  }

  // Forward declaration: the refresh thunk references `client` but
  // `client` isn't assigned until the call below resolves. Late
  // binding via the function reference avoids a temporal-dead-zone
  // throw and keeps the API surface clean.
  const client = createApiClient({
    baseURL,
    onRequestStart: markRequestStart,
    onRequestEnd: markRequestEnd,
    deps: {
      getAccessToken: () => session.accessToken,
      // CSRF lives in the session store (hydrated from localStorage on
      // boot, rewritten on every login/refresh from the X-EZJWT-CSRF
      // response header). No cookie fallback — `document.cookie` won't
      // contain it when SPA and API live on different hosts.
      getCsrfToken: () => session.csrfToken,
      async refresh() {
        try {
          const res = await refreshRequest(client)
          session.setTokens(res.access_token, res.expires_in, res.csrf)
          return 'ok'
        } catch (err) {
          // A refresh that never reached a server verdict (offline,
          // timeout, or a CORS-blocked preflight on /auth/refresh) is
          // transient — the session may still be valid, so report it as
          // `unreachable` and let the client surface the error instead
          // of logging the user out. Only a verdict the server actually
          // returned (rejected RT, CSRF mismatch → an HTTP response)
          // counts as real auth loss.
          if (err instanceof ApiError && (err.kind === 'network' || err.kind === 'timeout')) {
            return 'unreachable'
          }
          return 'failed'
        }
      },
      onAuthLost,
    },
  })
  return client
}

/**
 * Build the singleton api client. Must be called exactly once during
 * boot, after Pinia is installed (so `useSessionStore` resolves) and
 * before any composable calls `getApiClient()`. Idempotent — calling
 * twice returns the already-built singleton, the second `onAuthLost`
 * is ignored.
 */
export function initApiClient(options: InitApiClientOptions): ApiClient {
  if (_client) return _client
  _onAuthLost = options.onAuthLost
  _client = buildClient()
  return _client
}

/**
 * Read the singleton. Lazily rebuilds after `resetApiClient()` so the
 * domain switcher's "swap baseURL on dropdown change" flow doesn't
 * need to re-pass the navigation hook. Throws clearly if
 * `initApiClient` has never run — that's a boot-order bug, not a
 * runtime case to silently absorb.
 */
export function getApiClient(): ApiClient {
  if (_client) return _client
  if (!_onAuthLost) {
    throw new Error(
      'API client is not initialised — call initApiClient(...) from main.ts before any composable that uses the api.',
    )
  }
  _client = buildClient()
  return _client
}

/**
 * Tear down the singleton; the next `getApiClient()` call rebuilds it
 * with the (possibly new) baseURL from the session store. Used by the
 * domain switcher on the login screen — `_onAuthLost` is preserved so
 * the rebuilt client keeps the same navigation hook without the
 * caller re-supplying it.
 */
export function resetApiClient(): void {
  _client = null
}
